/**
 * Normalize raw Ant Design table filter state into the structure expected by the
 * Execution View backend FilterConfig model.
 *
 * Input:  flat object from table column filters, e.g. { cuisine: ["Italian"], is_crm_available: [1], ... }
 * Output: { outlet_filters, metric_filters, sku_filters, crm_filters, google_filters, brand_filter, brand_metric_filters }
 *
 * Shared by both fetchOutletsData (data table) and exportOutletsData (CSV export)
 * to ensure filters are applied identically in both paths.
 */
import { segmentNodesToFilters } from "../constants/segments";
import { territoryNodesToFilters } from "../constants/territories";
import {
  buildCustomFieldFilters,
  joinAiColumnSelection,
} from "../utils/aiColumnPresence";

export const normalizeExecutionViewFilters = (fo) => {
  // Keys that belong to brand_filter only
  const brandOnlyKeys = new Set([
    "has_brands_in_menu",
    "listing_brand_category",
    "not_listing_brand_category",
    "is_offering_brands",
    "not_offering_brands",
    "hasBrandsInMenu",
    "listingBrandCategory",
    "notListingBrandCategory",
    "isOfferingBrands",
    "notOfferingBrands",
    "brand_filter",
    "offeringCondition",
    "notOfferingConditionState",
    "listingCondition",
    "notListingCondition",
  ]);

  const outletFilterKeys = new Set([
    "cuisine",
    "platform",
    "franchise",
    "price_level",
    "segment_type",
    "segment_nodes",
    "geo_level_1",
    "geo_level_2",
    "territory_nodes",
    "postal_code",
    "city",
    "rating",
    "review",
    "chain_flag",
    "part_of_chain",
  ]);

  // CRM / Google filters should live in dedicated buckets
  const crmFilterKeys = new Set([
    "id_client_crm",
    "crm_client_id",
    "is_crm_available",
  ]);
  const googleFilterKeys = new Set(["place_id", "is_place_id_available"]);

  let brand_filter = fo?.brand_filter || {};
  let outlet_filters = {};
  let metric_filters = {};
  let sku_filters = {};
  let crm_filters = {};
  let google_filters = {};
  let brand_metric_filters = {};

  // Store conditions for later use
  let offeringCondition = fo?.offeringCondition || "or";
  let notOfferingCondition = fo?.notOfferingConditionState || "or";

  const sanitizeBuckets = () => {
    // Clean outlet_filters of misplaced keys
    Object.keys(outlet_filters).forEach((k) => {
      if (brandOnlyKeys.has(k)) {
        delete outlet_filters[k];
      }
      if (crmFilterKeys.has(k)) {
        if (k === "id_client_crm" || k === "crm_client_id") {
          crm_filters.id_client_crm = Array.isArray(outlet_filters[k])
            ? outlet_filters[k]
            : [outlet_filters[k]];
        } else if (k === "is_crm_available") {
          crm_filters.is_crm_available = outlet_filters[k];
        }
        delete outlet_filters[k];
      }
      if (googleFilterKeys.has(k)) {
        if (k === "place_id") {
          google_filters.place_id = Array.isArray(outlet_filters[k])
            ? outlet_filters[k]
            : [outlet_filters[k]];
        } else if (k === "is_place_id_available") {
          google_filters.is_place_id_available = outlet_filters[k];
        }
        delete outlet_filters[k];
      }
    });
    // Clean metric_filters of misplaced keys
    Object.keys(metric_filters).forEach((k) => {
      if (brandOnlyKeys.has(k)) {
        delete metric_filters[k];
      }
      if (crmFilterKeys.has(k)) {
        if (k === "id_client_crm" || k === "crm_client_id") {
          crm_filters.id_client_crm = Array.isArray(metric_filters[k])
            ? metric_filters[k]
            : [metric_filters[k]];
        } else if (k === "is_crm_available") {
          crm_filters.is_crm_available = metric_filters[k];
        }
        delete metric_filters[k];
      }
      if (googleFilterKeys.has(k)) {
        if (k === "place_id") {
          google_filters.place_id = Array.isArray(metric_filters[k])
            ? metric_filters[k]
            : [metric_filters[k]];
        } else if (k === "is_place_id_available") {
          google_filters.is_place_id_available = metric_filters[k];
        }
        delete metric_filters[k];
      }
    });
  };

  // If already wrapped as filter/outlet_filters/metric_filters
  if (fo?.filter) {
    outlet_filters = { ...(fo.filter.outlet_filters || {}) };
    metric_filters = { ...(fo.filter.metric_filters || {}) };
    sku_filters = fo.filter.sku_filters || {};
    if (fo.filter.brand_filter) {
      brand_filter = { ...brand_filter, ...fo.filter.brand_filter };
    }
    if (fo.filter.crm_filters) {
      crm_filters = { ...crm_filters, ...fo.filter.crm_filters };
    }
    if (fo.filter.google_filters) {
      google_filters = { ...google_filters, ...fo.filter.google_filters };
    }
    // Extract brand_metric_filters for the brand-metrics layout, but clean empty nested objects
    if (fo.filter.brand_metric_filters) {
      const cleanBrandMetricFilters = {};
      
      // Only copy brand_pct_name_accuracy if it has data
      if (fo.filter.brand_metric_filters.brand_pct_name_accuracy && 
          Object.keys(fo.filter.brand_metric_filters.brand_pct_name_accuracy).length > 0) {
        cleanBrandMetricFilters.brand_pct_name_accuracy = fo.filter.brand_metric_filters.brand_pct_name_accuracy;
      }
      
      // Only copy brand_pct_image_presence if it has data
      if (fo.filter.brand_metric_filters.brand_pct_image_presence && 
          Object.keys(fo.filter.brand_metric_filters.brand_pct_image_presence).length > 0) {
        cleanBrandMetricFilters.brand_pct_image_presence = fo.filter.brand_metric_filters.brand_pct_image_presence;
      }
      
      // Only assign if there's actual data
      if (Object.keys(cleanBrandMetricFilters).length > 0) {
        brand_metric_filters = cleanBrandMetricFilters;
      }
    }
    // If brand_filter mistakenly inside metric_filters, pull it out
    if (metric_filters.brand_filter) {
      brand_filter = { ...brand_filter, ...metric_filters.brand_filter };
      const { brand_filter: _bf, ...rest } = metric_filters;
      metric_filters = rest;
    }
  } else {
    const { brand_filter: bf, ...rest } = fo || {};
    brand_filter = { ...brand_filter, ...(bf || {}) };
    Object.entries(rest || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (brandOnlyKeys.has(key)) {
        // Store conditions
        if (key === "offeringCondition") {
          offeringCondition = value;
          return;
        }
        if (key === "notOfferingConditionState") {
          notOfferingCondition = value;
          return;
        }
        if (key === "listingCondition") {
          if (!brand_filter.brand_categories)
            brand_filter.brand_categories = {};
          brand_filter.brand_categories.include_op = String(
            value || "or"
          ).toUpperCase();
          return;
        }
        if (key === "notListingCondition") {
          if (!brand_filter.brand_categories)
            brand_filter.brand_categories = {};
          brand_filter.brand_categories.exclude_op = String(
            value || "or"
          ).toUpperCase();
          return;
        }

        // Build brand_filter from individual fields
        if (key === "hasBrandsInMenu") {
          brand_filter.has_brand_in_menu = String(value === "yes");
        } else if (key === "has_brands_in_menu") {
          const v = Array.isArray(value) ? value[0] : value;
          brand_filter.has_brand_in_menu = String(v === 1 || v === true);
        } else if (key === "listingBrandCategory") {
          if (!brand_filter.brand_categories)
            brand_filter.brand_categories = {};
          brand_filter.brand_categories.include = Array.isArray(value)
            ? value
            : [value];
        } else if (key === "listing_brand_category") {
          if (!brand_filter.brand_categories)
            brand_filter.brand_categories = {};
          brand_filter.brand_categories.include = Array.isArray(value)
            ? value
            : [value];
        } else if (key === "notListingBrandCategory") {
          if (!brand_filter.brand_categories)
            brand_filter.brand_categories = {};
          brand_filter.brand_categories.exclude = Array.isArray(value)
            ? value
            : [value];
        } else if (key === "not_listing_brand_category") {
          if (!brand_filter.brand_categories)
            brand_filter.brand_categories = {};
          brand_filter.brand_categories.exclude = Array.isArray(value)
            ? value
            : [value];
        } else if (
          key === "isOfferingBrands" ||
          key === "notOfferingBrands"
        ) {
          // These will be processed later from the filter object
          // Store temporarily for processing
          if (key === "isOfferingBrands") {
            brand_filter._isOfferingBrands_temp = value;
          } else {
            brand_filter._notOfferingBrands_temp = value;
          }
        } else if (
          key === "is_offering_brands" ||
          key === "not_offering_brands"
        ) {
          if (key === "is_offering_brands") {
            brand_filter._isOfferingBrands_temp = value;
          } else {
            brand_filter._notOfferingBrands_temp = value;
          }
        }
        return; // skip, don't add to outlet/metric
      }
      if (crmFilterKeys.has(key)) {
        // crm bucket
        if (key === "crm_client_id" || key === "id_client_crm") {
          crm_filters.id_client_crm = Array.isArray(value)
            ? value
            : [value];
        } else if (key === "is_crm_available") {
          crm_filters.is_crm_available = value;
        }
      } else if (googleFilterKeys.has(key)) {
        // google bucket
        if (key === "place_id") {
          google_filters.place_id = Array.isArray(value) ? value : [value];
        } else if (key === "is_place_id_available") {
          google_filters.is_place_id_available = value;
        }
      } else if (key.endsWith("_name_accuracy") || key.endsWith("_image_presence")) {
        // Brand-specific metric filters for the brand-metrics layout
        // e.g., "Red Bull Editions_name_accuracy" or "Red Bull Editions_image_presence"
        if (key.endsWith("_name_accuracy")) {
          // Extract brand name
          const brandName = key.replace(/_name_accuracy$/, "");
          if (!brand_metric_filters.brand_pct_name_accuracy) {
            brand_metric_filters.brand_pct_name_accuracy = {};
          }
          // Value is an array like [{from: 0, to: 100}] - convert to range
          if (Array.isArray(value) && value.length > 0 && value[0].from !== undefined) {
            brand_metric_filters.brand_pct_name_accuracy[brandName] = {
              min: value[0].from,
              max: value[0].to
            };
          }
        } else if (key.endsWith("_image_presence")) {
          // Extract brand name
          const brandName = key.replace(/_image_presence$/, "");
          if (!brand_metric_filters.brand_pct_image_presence) {
            brand_metric_filters.brand_pct_image_presence = {};
          }
          // Value is an array like [true] or [false]
          if (Array.isArray(value) && value.length > 0 && typeof value[0] === "boolean") {
            brand_metric_filters.brand_pct_image_presence[brandName] = value[0];
          }
        }
      } else if (outletFilterKeys.has(key)) {
        // Special handling for part_of_chain: convert array [1] or [0] to single value
        if (
          key === "part_of_chain" &&
          Array.isArray(value) &&
          value.length > 0
        ) {
          outlet_filters[key] = value[0];
        } else {
          outlet_filters[key] = value;
        }
      } else if (key.startsWith("custom_")) {
        // AI Column filter — handled by the dedicated custom_field_filters branch
        // below. Skip here so it doesn't pollute metric_filters.
      } else {
        metric_filters[key] = value;
      }
    });
  }

  // Process isOfferingBrands and notOfferingBrands
  const processBrandSelections = (selectedValues, condition) => {
    if (!selectedValues || selectedValues.length === 0) return null;
    const brands = [];
    const skus = [];
    selectedValues.forEach((item) => {
      try {
        const parsed = JSON.parse(item);
        if (typeof parsed === "object" && parsed !== null) {
          const skuObj = {};
          if (parsed.subbrand) skuObj.subbrand = parsed.subbrand;
          if (parsed.packaging_type)
            skuObj.packaging_type = parsed.packaging_type;
          if (parsed.packaging_size)
            skuObj.packaging_size = parsed.packaging_size;
          if (parsed.volume) skuObj.volume = parsed.volume;
          if (Object.keys(skuObj).length > 0) {
            skus.push(skuObj);
          }
        } else {
          brands.push(item);
        }
      } catch {
        brands.push(item);
      }
    });
    if (brands.length === 0 && skus.length === 0) return null;
    return {
      ...(brands.length > 0 && { brands }),
      ...(skus.length > 0 && { skus }),
      op: (condition || "or").toUpperCase(),
    };
  };

  if (
    brand_filter._isOfferingBrands_temp ||
    brand_filter._notOfferingBrands_temp
  ) {
    if (!brand_filter.brands) brand_filter.brands = {};

    if (brand_filter._isOfferingBrands_temp) {
      const includeData = processBrandSelections(
        brand_filter._isOfferingBrands_temp,
        offeringCondition
      );
      if (includeData) {
        brand_filter.brands.include = includeData;
      }
    }

    if (brand_filter._notOfferingBrands_temp) {
      const excludeData = processBrandSelections(
        brand_filter._notOfferingBrands_temp,
        notOfferingCondition
      );
      if (excludeData) {
        brand_filter.brands.exclude = excludeData;
      }
    }

    if (brand_filter.brands.include || brand_filter.brands.exclude) {
      brand_filter.brands.conflict_policy = "exclude_wins";
    }

    // Clean up temp fields
    delete brand_filter._isOfferingBrands_temp;
    delete brand_filter._notOfferingBrands_temp;
  }

  sanitizeBuckets();

  // Ant Design table filters wrap single values in arrays (e.g. is_crm_available: [1]).
  // Backend CRMFilters / GoogleFilters expect Optional[bool], so unwrap + coerce here.
  const coerceBool = (v) => {
    const val = Array.isArray(v) ? v[0] : v;
    if (val === undefined || val === null) return undefined;
    if (typeof val === "boolean") return val;
    if (val === 1 || val === "1" || val === "true") return true;
    if (val === 0 || val === "0" || val === "false") return false;
    return Boolean(val);
  };
  if (crm_filters.is_crm_available !== undefined) {
    const b = coerceBool(crm_filters.is_crm_available);
    if (b === undefined) delete crm_filters.is_crm_available;
    else crm_filters.is_crm_available = b;
  }
  if (google_filters.is_place_id_available !== undefined) {
    const b = coerceBool(google_filters.is_place_id_available);
    if (b === undefined) delete google_filters.is_place_id_available;
    else google_filters.is_place_id_available = b;
  }

  // Extract AI column filters: any flat key starting with `custom_<field_name>`
  // becomes an entry in custom_field_filters keyed by `<field_name>`. The
  // reserved "Has value" / "No value" options become `presence`, the rest
  // `values`. Backend `PetMetricsFilters.custom_field_filters`
  // (apps/api/src/schemas/pet_metrics.py) accepts this shape.
  const customFieldSelections = {};
  Object.entries(fo || {}).forEach(([key, vals]) => {
    if (!key.startsWith("custom_")) return;
    if (!Array.isArray(vals) || vals.length === 0) return;
    const fieldName = key.replace(/^custom_/, "");
    customFieldSelections[fieldName] = vals;
  });
  // Wrapped-shape input: pick up custom_field_filters that restructureFilterOptions
  // already routed under fo.filter.
  const wrapped = fo?.filter?.custom_field_filters;
  if (wrapped) {
    const fieldNames = new Set([
      ...Object.keys(wrapped.values || {}),
      ...Object.keys(wrapped.presence || {}),
    ]);
    fieldNames.forEach((fieldName) => {
      const selection = joinAiColumnSelection(
        wrapped.values?.[fieldName],
        wrapped.presence?.[fieldName]
      );
      if (selection.length > 0) customFieldSelections[fieldName] = selection;
    });
  }
  const customFieldFilters = buildCustomFieldFilters(customFieldSelections);

  const result = {
    brand_filter:
      Object.keys(brand_filter).length > 0 ? brand_filter : null,
    outlet_filters,
    metric_filters,
    sku_filters,
    crm_filters,
    google_filters,
    brand_metric_filters,
  };

  if (customFieldFilters) {
    result.custom_field_filters = customFieldFilters;
  }

  return result;
};

/**
 * Assemble the `filter` request body from a normalized filter object.
 *
 * Input: the output of normalizeExecutionViewFilters (buckets that may be empty
 * `{}`, a `brand_filter` that may be `null`, an optional `custom_field_filters`).
 * Output: an object containing only the non-empty filter buckets.
 *
 * Both the data-table fetch and the CSV export MUST build the body this way. An
 * empty bucket is not a no-op on the backend: the Execution View brand-metrics
 * template switches an outer join from LEFT to INNER on the mere presence of
 * `brand_metric_filters` (an empty `{}` hydrates into a truthy Pydantic model),
 * which drops every outlet absent from dws_brand_metrics. Stripping empty
 * buckets here keeps both paths returning the same rows.
 */
export const buildExecutionViewFilterBody = (normalized) => {
  const {
    brand_filter,
    outlet_filters,
    metric_filters,
    sku_filters,
    crm_filters,
    google_filters,
    brand_metric_filters,
    custom_field_filters,
  } = normalized || {};

  const filterObj = {};
  if (outlet_filters && Object.keys(outlet_filters).length > 0) {
    // `segment_nodes` carries the tree's checked keys so the modal can restore its own
    // checkboxes. The API takes {segment_type, sub_segment} nodes instead, so the keys
    // are decoded and pruned here, at the one boundary both the table fetch and the CSV
    // export cross.
    // `territory_nodes` works the same way: the tree's keys become the sales_region
    // and territory lists the API combines as a union.
    const { segment_nodes, territory_nodes, ...rest } = outlet_filters;
    const segmentFilters = segment_nodes
      ? segmentNodesToFilters(segment_nodes)
      : [];
    const withSegments = segmentFilters.length
      ? { ...rest, segment_filters: segmentFilters }
      : rest;
    const resolved = territory_nodes
      ? { ...withSegments, ...territoryNodesToFilters(territory_nodes) }
      : withSegments;
    // A bucket holding nothing but unusable segment keys must not ship as `{}`.
    if (Object.keys(resolved).length > 0) {
      filterObj.outlet_filters = resolved;
    }
  }
  if (metric_filters && Object.keys(metric_filters).length > 0) {
    filterObj.metric_filters = metric_filters;
  }
  if (sku_filters && Object.keys(sku_filters).length > 0) {
    filterObj.sku_filters = sku_filters;
  }
  if (
    brand_filter &&
    typeof brand_filter === "object" &&
    Object.keys(brand_filter).length > 0
  ) {
    filterObj.brand_filter = brand_filter;
  }
  if (crm_filters && Object.keys(crm_filters).length > 0) {
    filterObj.crm_filters = crm_filters;
  }
  if (google_filters && Object.keys(google_filters).length > 0) {
    filterObj.google_filters = google_filters;
  }
  if (
    custom_field_filters &&
    (Object.keys(custom_field_filters.values || {}).length > 0 ||
      Object.keys(custom_field_filters.presence || {}).length > 0)
  ) {
    filterObj.custom_field_filters = custom_field_filters;
  }
  // Only include brand_metric_filters if its nested objects hold real data.
  if (brand_metric_filters) {
    const hasNameAccuracy =
      brand_metric_filters.brand_pct_name_accuracy &&
      Object.keys(brand_metric_filters.brand_pct_name_accuracy).length > 0;
    const hasImagePresence =
      brand_metric_filters.brand_pct_image_presence &&
      Object.keys(brand_metric_filters.brand_pct_image_presence).length > 0;

    if (hasNameAccuracy || hasImagePresence) {
      const validBrandMetricFilters = {};
      if (hasNameAccuracy) {
        validBrandMetricFilters.brand_pct_name_accuracy =
          brand_metric_filters.brand_pct_name_accuracy;
      }
      if (hasImagePresence) {
        validBrandMetricFilters.brand_pct_image_presence =
          brand_metric_filters.brand_pct_image_presence;
      }
      filterObj.brand_metric_filters = validBrandMetricFilters;
    }
  }

  return filterObj;
};
